import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { STATES } from "@/consts/states";
import { useTRPC, type RouterOutputs } from "@/trpc";

type BillingAccountSummary = RouterOutputs["organization"]["getBillingAccount"];

const ADDRESS_LINE_MAX_LENGTH = 200;
const CITY_MAX_LENGTH = 100;
const STATE_CODES = new Set(STATES.map(([, code]) => code));
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
const STATE_CODE_LENGTH = 2;

const contactSchema = z.object({
  billingName: z.string().trim().min(1, "Billing name is required"),
  billingEmail: z.string().trim().email("Enter a valid email address"),
  line1: z
    .string()
    .trim()
    .min(1, "Address is required")
    .max(ADDRESS_LINE_MAX_LENGTH, "Address is too long"),
  line2: z
    .string()
    .trim()
    .max(ADDRESS_LINE_MAX_LENGTH, "Address line 2 is too long"),
  city: z
    .string()
    .trim()
    .min(1, "City is required")
    .max(CITY_MAX_LENGTH, "City is too long"),
  state: z
    .string()
    .trim()
    .refine(
      (state) => STATE_CODES.has(state),
      "Use the 2-letter state code, e.g. NY",
    ),
  postalCode: z
    .string()
    .trim()
    .regex(ZIP_PATTERN, "Enter a ZIP code like 12345 or 12345-6789"),
});

type ContactValues = z.infer<typeof contactSchema>;

export function BillingContactCard({
  organizationBusinessUnitId,
  summary,
  canManage,
}: {
  organizationBusinessUnitId: string;
  summary: BillingAccountSummary;
  canManage: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { account } = summary;
  const address = account?.billingAddress;

  const form = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      billingName: account?.billingName ?? summary.suggestedBillingName,
      billingEmail: account?.billingEmail ?? "",
      line1: address?.line1 ?? "",
      line2: address?.line2 ?? "",
      city: address?.city ?? "",
      state: address?.state ?? "",
      postalCode: address?.postalCode ?? "",
    },
  });

  const saveMutation = useMutation(
    trpc.organization.saveBillingContact.mutationOptions({
      onSuccess: async () => {
        toast.success("Billing contact saved");
        await queryClient.invalidateQueries(
          trpc.organization.getBillingAccount.queryFilter({
            organizationBusinessUnitId,
          }),
        );
      },
      onError: (error) => {
        toast.error("Couldn't save the billing contact", {
          description: error.message,
        });
      },
    }),
  );

  function onSubmit({ billingName, billingEmail, ...values }: ContactValues) {
    saveMutation.mutate({
      organizationBusinessUnitId,
      billingName,
      billingEmail,
      billingAddress: { ...values, line2: values.line2 || null },
      expectedVersion: account?.version ?? null,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing contact</CardTitle>
        <CardDescription>
          Invoices are addressed to this name and address and sent to this
          email. The address determines sales tax.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="billingName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!canManage} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="billingEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} disabled={!canManage} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <fieldset className="space-y-4">
              <legend className="text-sm font-medium">Billing address</legend>
              <FormField
                control={form.control}
                name="line1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address line 1</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="address-line1"
                        {...field}
                        disabled={!canManage}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="line2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address line 2 (optional)</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="address-line2"
                        {...field}
                        disabled={!canManage}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="address-level2"
                          {...field}
                          disabled={!canManage}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="address-level1"
                          maxLength={STATE_CODE_LENGTH}
                          {...field}
                          onChange={(event) =>
                            field.onChange(event.target.value.toUpperCase())
                          }
                          disabled={!canManage}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP code</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="postal-code"
                          inputMode="numeric"
                          {...field}
                          disabled={!canManage}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </fieldset>
            {canManage && (
              <div className="flex justify-end">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving..." : "Save contact"}
                </Button>
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
